import React, { useEffect, useRef, useState } from "react";
import "./login.css";
import { DocketAPIError, getAPIClient, legalSexFromString } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { errorLog, infoLog } from "../../utils/logger";
import ResponsiveDatePicker from "../../components/form/ResponsiveDatePicker";
import {
  checkBoxValueMap,
  getLocalizedLegalSexChoices,
  getResponsiveRadios,
} from "../../utils/formUtils";
import { DateTime } from "luxon";
import lodash from "lodash";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { useAtom } from "jotai";
import { biometricAuthAtom, userAtom } from "../../jotai/atoms";
import { BiometricAuth, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { Capacitor } from "@capacitor/core";
import { getBiometricLabel } from "../../utils/biometrics";
import { DocketSwitch } from "../../components/form/DocketSwitch";
import { registerPushPermission } from "../../pushNotifications";
import { vibrateDevice } from "../../utils/capacitor";

export function AccountInfo() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname, state } = useLocation();

  const [dob, setDob] = useState<Date | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState<boolean | string>(false);
  const [checkboxValue, setCheckboxValue] = useState(getLocalizedLegalSexChoices(t));
  const [biometricAuth, setBiometricAuth] = useAtom(biometricAuthAtom);
  const [biometryType, setBiometryType] = useState<BiometryType | null>(null);
  const [pushPermission, setPushPermission] = useState<boolean>(false);
  const lastNameRef = useRef<HTMLInputElement>(null);

  const checkboxHandler = (value: any, index: number) => {
    const newValue = checkBoxValueMap(checkboxValue, index);
    infoLog(`newValue ${JSON.stringify(newValue)}`);
    setCheckboxValue(newValue);
  };

  const [currentUser, setCurrentUser] = useAtom(userAtom);

  const registerNativeSettings = async () => {
    const biometry = await BiometricAuth.checkBiometry();
    if (biometry.isAvailable) {
      setBiometryType(biometry.biometryType);
    }

    const pushPermission = await registerPushPermission();
    if (pushPermission) {
      setPushPermission(true);
    }
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      registerNativeSettings();
    }
  }, []);

  useEffect(() => {
    if (currentUser?.dob) {
      const dobDateTime = DateTime.fromISO(currentUser.dob);
      if (dobDateTime.isValid) {
        setDob(dobDateTime.toJSDate());
      }
    }
    if (currentUser?.first_name) {
      setFirstName(currentUser.first_name);
    }
    if (currentUser?.last_name) {
      setLastName(currentUser.last_name);
    }
    if (currentUser?.legal_sex) {
      checkboxHandler(currentUser.legal_sex, 0);
    }
  }, [currentUser]);

  const toggleBiometricAuth = async () => {
    if (biometryType && !biometricAuth) {
      try {
        await BiometricAuth.authenticate();
        await vibrateDevice();
        setBiometricAuth(true);
      } catch (error) {
        setBiometricAuth(false);
      }
    } else {
      setBiometricAuth(false);
    }
  };

  const { handleSubmit } = useForm();

  const searchFormTranslations = {
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
    firstName: t("onboarding.first-name"),
    lastName: t("onboarding.last-name"),
  };

  const ageCheckPassed = () => {
    if (dob && DateTime.now().minus({ years: 13 }) <= DateTime.fromJSDate(dob)) {
      return false;
    }
    return true;
  };

  const onSubmit = async (data: any) => {
    if (!ageCheckPassed()) {
      errorLog({ message: t("settings.dob_error_message") } as Error);
      setShowErrorMessage(`${t("settings.dob_error_message")}`);
      return;
    }

    try {
      let sex = lodash.find(checkboxValue, ["checked", true]);
      let legalSex = sex?.value ?? "U";
      const params = {
        first_name: firstName,
        last_name: lastName,
        legal_sex: legalSex,
        dob: dob ? DateTime.fromJSDate(dob).toISODate() : null,
        language: "en",
        notification_preferences: {
          email_new_results: true,
          email_upcoming_vaccines: true,
          email_app_updates: true,
          push_new_results: pushPermission,
          push_upcoming_vaccines: pushPermission,
          push_app_updates: pushPermission,
        },
      };
      const res = await getAPIClient().updateUser(params);
      // save user info to db
      if (res) {
        await setCurrentUser({
          ...currentUser!,
          legal_sex: legalSexFromString(legalSex),
          dob: dob ? DateTime.fromJSDate(dob).toLocaleString() : null,
          first_name: firstName,
          last_name: lastName,
          language: "en",
          notification_preferences: {
            ...params.notification_preferences,
            disable_all: false,
          },
        });
        if (pathname === "/signup/accountinfo") {
          navigate("../verifyphone");
        } else {
          navigate("/home/settings");
        }
      }
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    }
  };

  const handleKeyDownFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => {
        lastNameRef.current?.focus();
      }, 50);
    }
  };

  const handleKeyDownLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      lastNameRef.current?.blur();
    }
  };

  return (
    <div
      className={`flex flex-col gap-4 h-full ${pathname === "/signup/accountinfo" ? "p-8" : ""}`}
    >
      {pathname === "/signup/accountinfo" && (
        <div className="flex flex-col">
          <p className="text-2xl md:text-black mobile-text-color text-center">
            {t("onboarding.signup_header")}
          </p>
          <div className="separator max-md:hidden"></div>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
        <div className="flex flex-col gap-6">
          {showErrorMessage && (
            <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
              {showErrorMessage}
            </ErrorModal>
          )}
          <fieldset className="fieldset">
            <legend className="fieldset-legend py-0 text-[16px] font-[700] text-black max-md:text-dark-blue max-md:dark:text-white">
              {searchFormTranslations.firstName}*
            </legend>
            <input
              className="input max-md:text-lg w-full input-responsive-white text-black max-md:!rounded-md"
              type="text"
              id={"firstName"}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={searchFormTranslations.firstName}
              onKeyDown={handleKeyDownFirstName}
            />
          </fieldset>
          <fieldset className="fieldset">
            <legend className="fieldset-legend py-0 text-[16px] font-[700] text-black max-md:text-dark-blue max-md:dark:text-white">
              {searchFormTranslations.lastName}*
            </legend>
            <input
              className="input max-md:text-lg w-full input-responsive-white text-black max-md:!rounded-md"
              id={"lastName"}
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={searchFormTranslations.lastName}
              ref={lastNameRef}
              onKeyDown={handleKeyDownLastName}
            />
          </fieldset>
          <div>
            <label className="text-[16px] font-[700] text-black max-md:text-dark-blue max-md:mb-0 max-md:dark:text-white">
              {searchFormTranslations.dateOfBirth}*
            </label>
            <div className="mobile-text-color">
              <ResponsiveDatePicker
                key="dateOfBirth"
                selectedDate={dob}
                setSelectedDate={(d) => setDob(d)}
                id="dateOfBirth"
              />
            </div>
          </div>
          {getResponsiveRadios(
            checkboxValue,
            searchFormTranslations.legalSex,
            checkboxHandler,
            "",
            "gender",
            t
          )}
          <div className="text-xs text-black max-md:text-dark-blue max-md:dark:text-white">
            *{t("system.required")}
          </div>
          {pathname === "/signup/accountinfo" && biometryType && (
            <div className="flex docket-gap-2">
              <p className="text-[16px] font-[700] text-dark-blue">
                Use {getBiometricLabel(biometryType)}
              </p>
              <DocketSwitch
                checked={biometricAuth}
                onChange={toggleBiometricAuth}
                id="biometric-auth"
                name="biometric-auth"
                data-testid="biometric-auth-toggle"
              />
            </div>
          )}
          <button
            className="btn docket-button-responsive w-full max-md:!mt-10 md:!mt-auto text-lg"
            type="submit"
            disabled={
              firstName.length < 2 ||
              lastName.length < 2 ||
              !dob ||
              !lodash.find(checkboxValue, ["checked", true])
            }
          >
            {t("generic.continue")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AccountInfo;
